import { Box, Grid, Paper, Typography, alpha, Skeleton } from '@mui/material';
import {
  DirectionsCar, DeliveryDining, Build, PendingActions, Schedule,
  Person, PersonOff, Work, LocalShipping, Engineering, Warning,
  Cancel, AttachMoney, Speed, Map as MapIcon,
} from '@mui/icons-material';
import type { DispatchDashboardStats } from '../../services/dispatcherService';
import { DISPATCH, panelShell } from './dispatchTheme';
import { formatMoney } from '../../utils/currency';

interface WidgetProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
}

function Widget({ label, value, icon, color, suffix }: WidgetProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        height: '100%',
        borderRadius: `${DISPATCH.radiusSm}px`,
        border: `1px solid ${alpha(color, 0.16)}`,
        bgcolor: alpha(color, 0.04),
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: DISPATCH.shadow },
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" lineHeight={1.2} noWrap>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={color} lineHeight={1.25} mt={0.25} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            {value}{suffix}
          </Typography>
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(color, 0.12), color, flexShrink: 0 }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
}

interface DispatchDashboardWidgetsProps {
  stats?: DispatchDashboardStats;
  loading?: boolean;
  compact?: boolean;
}

export default function DispatchDashboardWidgets({ stats, loading, compact }: DispatchDashboardWidgetsProps) {
  if (loading || !stats) {
    return (
      <Grid container spacing={1.25} sx={{ p: { xs: 1.25, sm: 1.5 } }}>
        {Array.from({ length: compact ? 4 : 8 }).map((_, i) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
            <Skeleton variant="rounded" height={68} sx={{ borderRadius: `${DISPATCH.radiusSm}px` }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const allWidgets: WidgetProps[] = [
    { label: 'Active Rides', value: stats.activeRides, icon: <DirectionsCar fontSize="small" />, color: '#6366f1' },
    { label: 'Deliveries', value: stats.activeDeliveries, icon: <DeliveryDining fontSize="small" />, color: '#ea580c' },
    { label: 'Services', value: stats.activeServiceRequests, icon: <Build fontSize="small" />, color: '#0891b2' },
    { label: 'Pending', value: stats.pendingAssignments, icon: <PendingActions fontSize="small" />, color: '#f59e0b' },
    { label: 'Scheduled', value: stats.scheduledBookings, icon: <Schedule fontSize="small" />, color: '#7c3aed' },
    { label: 'Online Drivers', value: stats.onlineDrivers, icon: <Person fontSize="small" />, color: '#10b981' },
    { label: 'Busy Drivers', value: stats.busyDrivers, icon: <Work fontSize="small" />, color: '#3b82f6' },
    { label: 'Offline', value: stats.offlineDrivers, icon: <PersonOff fontSize="small" />, color: '#94a3b8' },
    { label: 'Delivery Partners', value: stats.availableDeliveryPartners, icon: <LocalShipping fontSize="small" />, color: '#16a34a' },
    { label: 'Providers', value: stats.availableServiceProviders, icon: <Engineering fontSize="small" />, color: '#0d9488' },
    { label: 'SOS', value: stats.activeSos, icon: <Warning fontSize="small" />, color: '#ef4444' },
    { label: 'Cancelled', value: stats.cancellationsToday, icon: <Cancel fontSize="small" />, color: '#64748b' },
    { label: 'Revenue', value: `${formatMoney(stats.revenueToday)}`, icon: <AttachMoney fontSize="small" />, color: '#15803d' },
    { label: 'Utilization', value: stats.driverUtilization, icon: <Speed fontSize="small" />, color: '#6366f1', suffix: '%' },
    { label: 'Heat Points', value: stats.heatMapPoints?.length || 0, icon: <MapIcon fontSize="small" />, color: '#db2777' },
  ];

  const widgets = compact ? allWidgets.slice(0, 6) : allWidgets;

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
      <Grid container spacing={1.25}>
        {widgets.map((w) => (
          <Grid item xs={6} sm={4} md={3} lg={compact ? 4 : 2} key={w.label}>
            <Widget {...w} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
